import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelectionStore } from "../../stores/selectionStore";
import {
  getSceneState,
  useBodies,
  useMaterials,
  useMeshes,
  useSceneActions,
} from "../../stores/sceneStore";
import { usePhysicsStore } from "../../stores/physicsStore";
import { getHistoryActions } from "../../stores/historyStore";
import { useViewerActions, useViewerStore } from "../../stores/viewerStore";
import { dispatchSelectionHighlightUiState } from "../../selected-highlight-visibility/visibilityEvents";
import {
  clearMeshInspectorBoundingBoxOverlays,
  renderMultiBodySelectionBoundingBox,
  clearMultiBodySelectionBoundingBox,
  renderMeshInspectorBoundingBoxes,
  setMeshInspectorOrientationAxes,
} from "../../selected-mesh-visibility/inspectorOverlays";
import {
  MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
  renderMeshWireframeOverlayForMeshNames,
} from "../../selected-mesh-visibility/wireframeOverlay";
import {
  clearSelectedVisualMeshes,
  setSelectedVisualMeshIds,
  useSelectedVisualMeshIds,
} from "../../visual-mesh-multi-selection";
import { resolveVisualMeshTransformTargets } from "../../utils/visualMeshTransformTargets";
import { stripNamespacePrefix } from "../../utils/nameUtils";
import {
  getVisibleMeshCenterOriginLocal,
  getVisibleMeshStartOriginLocal,
  moveMeshOriginToLocalPointPreservingInstances,
  setMeshOriginPositionPreservingInstances,
} from "./meshOriginEditing";

const stripInspectorPrefix = stripNamespacePrefix;

export function useInspectorMultiSelection({
  clearAllSelections,
  selectedBodies,
  setLastSelectionSource,
  setSelectedBodies,
}) {
  const selectedVisualMeshIds = useSelectedVisualMeshIds();
  const selectedVisualMeshCount = selectedVisualMeshIds.size;
  const isPhysicsRunning = usePhysicsStore((s) => s.isRunning);
  const bodyVisibility = useViewerStore((s) => s.bodyVisibility);
  const meshVisibility = useViewerStore((s) => s.meshVisibility);
  const viewerActions = useViewerActions();
  const sceneActions = useSceneActions();
  const allBodies = useBodies();
  const allMaterials = useMaterials();
  const allMeshes = useMeshes();

  const [multiBoundingBox, setMultiBoundingBox] = useState(false);
  const [multiHighlight, setMultiHighlight] = useState(() => selectedBodies.length > 1);
  const [multiFocus, setMultiFocus] = useState(false);
  const [multiMeshHighlight, setMultiMeshHighlight] = useState(
    () => selectedVisualMeshIds.size > 1
  );
  const [multiMeshWireframe, setMultiMeshWireframe] = useState(false);
  const [multiMeshBoundingBox, setMultiMeshBoundingBox] = useState(false);
  const [multiMeshOriginMarker, setMultiMeshOriginMarker] = useState(false);
  const [multiMeshWireframeColor, setMultiMeshWireframeColor] = useState("#ff0000");
  const [sceneAssetsCompleteTick, setSceneAssetsCompleteTick] = useState(0);

  const multiBodyChildUuids = useBodies(
    (bodies) => {
      if (selectedBodies.length <= 1) return [];
      const parentMap = new Map();
      for (const [uuid, body] of Object.entries(bodies || {})) {
        if (!parentMap.has(uuid)) parentMap.set(uuid, null);
        for (const childUuid of body?.children ?? []) {
          if (typeof childUuid === "string") parentMap.set(childUuid, uuid);
        }
      }
      const selectedBodySet = new Set(selectedBodies);
      return selectedBodies.filter((uuid) => {
        let currentUuid = parentMap.get(uuid);
        while (currentUuid != null) {
          if (selectedBodySet.has(currentUuid)) return true;
          currentUuid = parentMap.get(currentUuid);
        }
        return false;
      });
    },
    (a, b) => a.length === b.length && a.every((value, index) => value === b[index])
  );

  const handleMultiBodyToggleVisibility = useCallback(() => {
    if (isPhysicsRunning || selectedBodies.length === 0) return;
    const allVisible = selectedBodies.every((uuid) => bodyVisibility[uuid] !== false);
    selectedBodies.forEach((uuid) => viewerActions.setBodyVisibility(uuid, !allVisible));
  }, [bodyVisibility, isPhysicsRunning, selectedBodies, viewerActions]);

  const handleMultiMeshToggleVisibility = useCallback(() => {
    if (isPhysicsRunning || selectedVisualMeshIds.size === 0) return;
    const meshUuids = Array.from(selectedVisualMeshIds);
    const allVisible = meshUuids.every((meshUuid) => meshVisibility[meshUuid] !== false);
    meshUuids.forEach((meshUuid) => viewerActions.setMeshVisibility(meshUuid, !allVisible));
  }, [isPhysicsRunning, meshVisibility, selectedVisualMeshIds, viewerActions]);

  useEffect(() => {
    if (selectedVisualMeshIds.size === 0) return;
    const next = new Set([...selectedVisualMeshIds].filter((meshUuid) => allMeshes?.[meshUuid]));
    if (next.size !== selectedVisualMeshIds.size) {
      setSelectedVisualMeshIds(next);
    }
  }, [allMeshes, selectedVisualMeshIds]);

  const visualMeshMaterialOptions = useMemo(
    () =>
      Object.values(allMaterials || {})
        .filter((material) => material?.uuid)
        .map((material) => material)
        .sort((a, b) =>
          String(stripInspectorPrefix(a.name) || a.uuid || "").localeCompare(
            String(stripInspectorPrefix(b.name) || b.uuid || ""),
            undefined,
            {
              numeric: true,
              sensitivity: "base",
            }
          )
        ),
    [allMaterials]
  );

  const selectedVisualMeshMaterialIds = useMemo(() => {
    const materialIds = new Set();
    Object.values(allBodies || {}).forEach((body) => {
      (body?.geoms || []).forEach((geom) => {
        if (!geom?.mesh || !selectedVisualMeshIds.has(geom.mesh)) return;
        const materialId = Array.isArray(geom.materials) ? geom.materials[0] : "";
        if (materialId) materialIds.add(materialId);
      });
    });
    return materialIds;
  }, [allBodies, selectedVisualMeshIds]);

  const selectedVisualMeshMaterialLabel = useMemo(() => {
    if (selectedVisualMeshCount === 0) return "";
    if (selectedVisualMeshMaterialIds.size !== 1) return "Mixed materials";
    const [materialId] = [...selectedVisualMeshMaterialIds];
    const material = allMaterials?.[materialId];
    return stripInspectorPrefix(material?.name) || materialId;
  }, [allMaterials, selectedVisualMeshCount, selectedVisualMeshMaterialIds]);

  const selectedVisualMeshTransformState = useMemo(
    () => resolveVisualMeshTransformTargets(allBodies, selectedVisualMeshIds),
    [allBodies, selectedVisualMeshIds]
  );

  const selectedVisualMeshVisibilityTargets = useMemo(() => {
    const meshIds = new Set([...selectedVisualMeshIds].map(String));
    const targets = [];
    Object.values(allBodies || {}).forEach((body) => {
      if (!body?.uuid || !Array.isArray(body.geoms)) return;
      body.geoms.forEach((geom) => {
        if (geom?.visualOnly !== true || !geom.mesh || !meshIds.has(String(geom.mesh))) return;
        targets.push({ meshUuid: String(geom.mesh), bodyUuid: String(body.uuid) });
      });
    });
    return targets;
  }, [allBodies, selectedVisualMeshIds]);

  const selectedBodySummaryItems = useMemo(
    () =>
      selectedBodies.map((bodyUuid) => ({
        id: bodyUuid,
        label: stripInspectorPrefix(allBodies?.[bodyUuid]?.name) || bodyUuid,
      })),
    [allBodies, selectedBodies]
  );

  const selectedVisualMeshSummaryItems = useMemo(
    () =>
      Array.from(selectedVisualMeshIds).map((meshUuid) => ({
        id: meshUuid,
        label: stripInspectorPrefix(allMeshes?.[meshUuid]?.name) || meshUuid,
      })),
    [allMeshes, selectedVisualMeshIds]
  );

  const handleRemoveBodyFromMultiSelection = useCallback(
    (bodyUuid) => {
      const next = new Set(selectedBodies);
      next.delete(bodyUuid);
      setSelectedBodies([...next]);
    },
    [selectedBodies, setSelectedBodies]
  );

  const handleRemoveVisualMeshFromMultiSelection = useCallback(
    (meshUuid) => {
      const next = new Set(selectedVisualMeshIds);
      next.delete(meshUuid);
      setSelectedVisualMeshIds(next);
      if (next.size === 1) {
        setLastSelectionSource("sceneTree");
        useSelectionStore.getState().selectMesh([...next][0]);
      } else if (next.size === 0) {
        clearAllSelections();
      }
    },
    [clearAllSelections, selectedVisualMeshIds, setLastSelectionSource]
  );

  const handleMultiMeshAssignMaterial = useCallback(
    (materialId) => {
      if (!materialId || selectedVisualMeshIds.size === 0 || isPhysicsRunning) return;
      const historyActions = getHistoryActions();
      historyActions.beginGroup(getSceneState());
      try {
        [...selectedVisualMeshIds].forEach((meshUuid) => {
          sceneActions.setMeshMaterial(meshUuid, materialId);
        });
      } finally {
        historyActions.endGroup(getSceneState());
      }
    },
    [isPhysicsRunning, sceneActions, selectedVisualMeshIds]
  );

  const refreshMultiMeshSnapshotOverlays = useCallback(() => {
    if (multiMeshBoundingBox) {
      renderMeshInspectorBoundingBoxes(selectedVisualMeshVisibilityTargets);
    }
  }, [multiMeshBoundingBox, selectedVisualMeshVisibilityTargets]);

  const handleMultiMeshOriginDeltaChange = useCallback(
    (delta) => {
      if (
        isPhysicsRunning ||
        !selectedVisualMeshTransformState.canTransform ||
        !Array.isArray(delta)
      ) {
        return;
      }
      const historyActions = getHistoryActions();
      historyActions.beginGroup(getSceneState());
      try {
        selectedVisualMeshTransformState.targets.forEach((target) => {
          moveMeshOriginToLocalPointPreservingInstances({
            meshUuid: target.meshUuid,
            sceneActions,
            selectedBodyUuid: target.bodyUuid,
            selectedGeomUuid: target.geomId,
            targetOriginLocalInput: delta,
          });
        });
      } finally {
        historyActions.endGroup(getSceneState());
      }
      requestAnimationFrame(refreshMultiMeshSnapshotOverlays);
    },
    [
      isPhysicsRunning,
      refreshMultiMeshSnapshotOverlays,
      sceneActions,
      selectedVisualMeshTransformState,
    ]
  );

  const handleMultiMeshSetCenterOrigin = useCallback(() => {
    if (isPhysicsRunning || !selectedVisualMeshTransformState.canTransform) return;
    const historyActions = getHistoryActions();
    historyActions.beginGroup(getSceneState());
    try {
      selectedVisualMeshTransformState.targets.forEach((target) => {
        const center = getVisibleMeshCenterOriginLocal({
          bodyUuid: target.bodyUuid,
          geomUuid: target.geomId,
          meshUuid: target.meshUuid,
        });
        if (!center) return;
        moveMeshOriginToLocalPointPreservingInstances({
          meshUuid: target.meshUuid,
          sceneActions,
          selectedBodyUuid: target.bodyUuid,
          selectedGeomUuid: target.geomId,
          targetOriginLocalInput: center,
        });
      });
    } finally {
      historyActions.endGroup(getSceneState());
    }
    requestAnimationFrame(refreshMultiMeshSnapshotOverlays);
  }, [
    isPhysicsRunning,
    refreshMultiMeshSnapshotOverlays,
    sceneActions,
    selectedVisualMeshTransformState,
  ]);

  const handleMultiMeshSetStartOrigin = useCallback(() => {
    if (isPhysicsRunning || !selectedVisualMeshTransformState.canTransform) return;
    const historyActions = getHistoryActions();
    historyActions.beginGroup(getSceneState());
    try {
      selectedVisualMeshTransformState.targets.forEach((target) => {
        const start = getVisibleMeshStartOriginLocal({
          bodyUuid: target.bodyUuid,
          geomUuid: target.geomId,
          meshUuid: target.meshUuid,
        });
        if (!start) return;
        moveMeshOriginToLocalPointPreservingInstances({
          meshUuid: target.meshUuid,
          sceneActions,
          selectedBodyUuid: target.bodyUuid,
          selectedGeomUuid: target.geomId,
          targetOriginLocalInput: start,
        });
      });
    } finally {
      historyActions.endGroup(getSceneState());
    }
    requestAnimationFrame(refreshMultiMeshSnapshotOverlays);
  }, [
    isPhysicsRunning,
    refreshMultiMeshSnapshotOverlays,
    sceneActions,
    selectedVisualMeshTransformState,
  ]);

  const handleMultiMeshResetOrigins = useCallback(() => {
    if (isPhysicsRunning || !selectedVisualMeshTransformState.canTransform) return;
    const historyActions = getHistoryActions();
    historyActions.beginGroup(getSceneState());
    try {
      selectedVisualMeshTransformState.targets.forEach((target) => {
        setMeshOriginPositionPreservingInstances({
          meshUuid: target.meshUuid,
          nextOriginPositionInput: [0, 0, 0],
          sceneActions,
          selectedBodyUuid: target.bodyUuid,
          selectedGeomUuid: target.geomId,
        });
      });
    } finally {
      historyActions.endGroup(getSceneState());
    }
    requestAnimationFrame(refreshMultiMeshSnapshotOverlays);
  }, [
    isPhysicsRunning,
    refreshMultiMeshSnapshotOverlays,
    sceneActions,
    selectedVisualMeshTransformState,
  ]);

  const prevSelectedBodiesSizeRef = useRef(0);
  useEffect(() => {
    const prev = prevSelectedBodiesSizeRef.current;
    const size = selectedBodies.length;
    prevSelectedBodiesSizeRef.current = size;
    if (prev > 1 && size <= 1) {
      setMultiBoundingBox(false);
      setMultiHighlight(false);
      setMultiFocus(false);
    } else if (prev <= 1 && size > 1) {
      setMultiHighlight(true);
    }
  }, [selectedBodies]);

  useEffect(() => {
    if (selectedBodies.length > 1 && multiBoundingBox) {
      renderMultiBodySelectionBoundingBox(selectedBodies);
    } else {
      clearMultiBodySelectionBoundingBox();
    }
    return () => {
      clearMultiBodySelectionBoundingBox();
    };
  }, [multiBoundingBox, selectedBodies]);

  useEffect(() => {
    if (selectedBodies.length <= 1) return undefined;
    dispatchSelectionHighlightUiState("body", multiHighlight);
    return () => {
      dispatchSelectionHighlightUiState("body", false);
    };
  }, [multiHighlight, selectedBodies]);

  useEffect(() => {
    const selectionActions = useSelectionStore.getState();
    if (selectedBodies.length > 1 && multiFocus) {
      const firstUuid = selectedBodies[0];
      selectionActions.setFocusMode(true, firstUuid);
    } else {
      selectionActions.clearFocusMode();
    }
    return () => {
      useSelectionStore.getState().clearFocusMode();
    };
  }, [multiFocus, selectedBodies]);

  const prevSelectedVisualMeshSizeRef = useRef(0);
  useEffect(() => {
    const prev = prevSelectedVisualMeshSizeRef.current;
    const size = selectedVisualMeshIds.size;
    prevSelectedVisualMeshSizeRef.current = size;
    if (prev > 1 && size <= 1) {
      setMultiMeshHighlight(false);
      setMultiMeshWireframe(false);
      setMultiMeshBoundingBox(false);
      setMultiMeshOriginMarker(false);
    } else if (prev <= 1 && size > 1) {
      setMultiMeshHighlight(true);
    }
  }, [selectedVisualMeshIds]);

  useEffect(() => {
    if (selectedVisualMeshIds.size <= 1) return undefined;
    dispatchSelectionHighlightUiState("mesh", multiMeshHighlight);
    return () => {
      dispatchSelectionHighlightUiState("mesh", false);
    };
  }, [multiMeshHighlight, selectedVisualMeshIds]);

  useEffect(() => {
    const meshUuids = Array.from(selectedVisualMeshIds);
    if (selectedVisualMeshIds.size > 1 && multiMeshWireframe) {
      if (!useViewerStore.getState().showVisual) viewerActions.toggleShowVisual();
      renderMeshWireframeOverlayForMeshNames(meshUuids, {
        visible: true,
        color: multiMeshWireframeColor,
        overlayName: MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
      });
    } else {
      renderMeshWireframeOverlayForMeshNames([], {
        visible: false,
        color: multiMeshWireframeColor,
        overlayName: MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
      });
    }
    return () => {
      renderMeshWireframeOverlayForMeshNames([], {
        visible: false,
        color: multiMeshWireframeColor,
        overlayName: MESH_INSPECTOR_WIREFRAME_OVERLAY_NAME,
      });
    };
  }, [multiMeshWireframe, multiMeshWireframeColor, selectedVisualMeshIds, viewerActions]);

  useEffect(() => {
    if (selectedVisualMeshIds.size > 1 && multiMeshBoundingBox) {
      if (!useViewerStore.getState().showVisual) viewerActions.toggleShowVisual();
      renderMeshInspectorBoundingBoxes(selectedVisualMeshVisibilityTargets);
    } else {
      clearMeshInspectorBoundingBoxOverlays();
    }
    return () => {
      clearMeshInspectorBoundingBoxOverlays();
    };
  }, [
    multiMeshBoundingBox,
    sceneAssetsCompleteTick,
    selectedVisualMeshIds,
    selectedVisualMeshVisibilityTargets,
    viewerActions,
  ]);

  useEffect(() => {
    if (selectedVisualMeshIds.size > 1 && multiMeshOriginMarker) {
      if (!useViewerStore.getState().showVisual) viewerActions.toggleShowVisual();
      selectedVisualMeshVisibilityTargets.forEach((target) => {
        setMeshInspectorOrientationAxes(target.meshUuid, true, target.bodyUuid);
      });
    } else {
      selectedVisualMeshVisibilityTargets.forEach((target) => {
        setMeshInspectorOrientationAxes(target.meshUuid, false, target.bodyUuid);
      });
    }
    return () => {
      selectedVisualMeshVisibilityTargets.forEach((target) => {
        setMeshInspectorOrientationAxes(target.meshUuid, false, target.bodyUuid);
      });
    };
  }, [
    multiMeshOriginMarker,
    sceneAssetsCompleteTick,
    selectedVisualMeshIds,
    selectedVisualMeshVisibilityTargets,
    viewerActions,
  ]);

  useEffect(() => {
    if (!multiMeshBoundingBox && !multiMeshOriginMarker) return undefined;
    const handleSceneAssetsComplete = () => {
      setSceneAssetsCompleteTick((tick) => tick + 1);
    };
    window.addEventListener("scene-build-assets-complete", handleSceneAssetsComplete);
    return () => {
      window.removeEventListener("scene-build-assets-complete", handleSceneAssetsComplete);
    };
  }, [multiMeshBoundingBox, multiMeshOriginMarker]);

  return {
    bodyVisibility,
    clearSelectedVisualMeshes,
    handleMultiBodyToggleVisibility,
    handleMultiMeshAssignMaterial,
    handleMultiMeshOriginDeltaChange,
    handleMultiMeshResetOrigins,
    handleMultiMeshSetCenterOrigin,
    handleMultiMeshSetStartOrigin,
    handleMultiMeshToggleVisibility,
    handleRemoveBodyFromMultiSelection,
    handleRemoveVisualMeshFromMultiSelection,
    isPhysicsRunning,
    meshVisibility,
    multiBodyChildUuids,
    multiBoundingBox,
    multiFocus,
    multiHighlight,
    multiMeshBoundingBox,
    multiMeshHighlight,
    multiMeshOriginMarker,
    multiMeshWireframe,
    multiMeshWireframeColor,
    selectedBodySummaryItems,
    selectedVisualMeshCount,
    selectedVisualMeshIds,
    selectedVisualMeshMaterialIds,
    selectedVisualMeshMaterialLabel,
    selectedVisualMeshSummaryItems,
    selectedVisualMeshTransformState,
    setMultiBoundingBox,
    setMultiFocus,
    setMultiHighlight,
    setMultiMeshBoundingBox,
    setMultiMeshHighlight,
    setMultiMeshOriginMarker,
    setMultiMeshWireframe,
    setMultiMeshWireframeColor,
    visualMeshMaterialOptions,
  };
}
